import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const CLASS_OPTIONS = [1, 2, 3, 4, 5];

/** Admin panel: manage make → class overrides (add/edit/delete). */
export default function ClassOverridesPanel({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ make: "", base_class: 2, v8_class: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/class-overrides");
      setRows(data);
    } catch {
      toast.error("Failed to load overrides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const make = form.make.trim();
    if (!make) return toast.error("Enter a make");
    setSaving(true);
    try {
      await api.post("/admin/class-overrides", {
        make,
        base_class: Number(form.base_class),
        v8_class: form.v8_class ? Number(form.v8_class) : null,
      });
      toast.success(`Override saved for ${make}`);
      setForm({ make: "", base_class: 2, v8_class: "" });
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const edit = (row) => {
    setForm({
      make: row.make,
      base_class: row.base_class,
      v8_class: row.v8_class || "",
    });
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove class override for ${row.make}?`)) return;
    try {
      await api.delete(`/admin/class-overrides/${row.id}`);
      toast.success("Override removed");
      await load();
      onChange && onChange();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8" data-testid="class-overrides-card">
      <div className="flex items-center gap-2 mb-2">
        <Pencil size={16} className="text-[#D4AF37]" />
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
          Class overrides
        </div>
      </div>
      <p className="text-xs text-[#6C757D] mb-6 leading-relaxed max-w-2xl">
        Add, edit, or remove make → class assignments on top of the uploaded
        rate sheet. Set an optional V8 class to route 8-cylinder vehicles into
        a different class (e.g. Nissan → Class 2 base, Class 3 for V8).
      </p>

      {/* Add / edit form */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_120px] gap-3 items-end mb-8">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
            Make
          </Label>
          <Input
            value={form.make}
            onChange={(e) => setForm({ ...form, make: e.target.value })}
            placeholder="e.g. Nissan"
            className="mt-1.5 h-10 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
            data-testid="override-make-input"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
            Base class
          </Label>
          <Select
            value={String(form.base_class)}
            onValueChange={(v) => setForm({ ...form, base_class: Number(v) })}
          >
            <SelectTrigger className="mt-1.5 h-10 rounded-sm border-[#0B132B]/15" data-testid="override-base-class-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CLASS_OPTIONS.map((c) => (
                <SelectItem key={c} value={String(c)}>Class {c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
            V8 class <span className="text-[#6C757D] font-normal">(optional)</span>
          </Label>
          <Select
            value={form.v8_class ? String(form.v8_class) : "none"}
            onValueChange={(v) => setForm({ ...form, v8_class: v === "none" ? "" : Number(v) })}
          >
            <SelectTrigger className="mt-1.5 h-10 rounded-sm border-[#0B132B]/15" data-testid="override-v8-class-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— none —</SelectItem>
              {CLASS_OPTIONS.map((c) => (
                <SelectItem key={c} value={String(c)}>Class {c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={save}
          disabled={saving || !form.make.trim()}
          className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
          data-testid="override-save-button"
        >
          <Plus size={14} className="mr-1.5" /> Save
        </Button>
      </div>

      {/* Existing overrides */}
      {loading ? (
        <div className="text-sm text-[#6C757D]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[#6C757D] italic">No overrides yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="overrides-table">
            <thead>
              <tr className="bg-[#0B132B] text-white text-xs uppercase tracking-[0.14em]">
                <th className="text-left px-3 py-2.5 font-semibold">Make</th>
                <th className="text-left px-3 py-2.5 font-semibold">Base class</th>
                <th className="text-left px-3 py-2.5 font-semibold">V8 class</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#E9ECEF]" data-testid={`override-row-${r.make.toLowerCase()}`}>
                  <td className="px-3 py-2.5 font-semibold text-[#0B132B]">{r.make}</td>
                  <td className="px-3 py-2.5 text-[#0B132B]">Class {r.base_class}</td>
                  <td className="px-3 py-2.5 text-[#0B132B]">
                    {r.v8_class ? `Class ${r.v8_class}` : <span className="text-[#6C757D]">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button size="sm" variant="ghost" onClick={() => edit(r)} className="h-8 rounded-sm mr-1" data-testid={`override-edit-${r.make.toLowerCase()}`}>
                      <Pencil size={12} className="mr-1.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r)} className="h-8 rounded-sm text-red-600 hover:bg-red-50" data-testid={`override-delete-${r.make.toLowerCase()}`}>
                      <Trash2 size={12} className="mr-1.5" /> Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
